import {
  api,
  token,
  ApiError,
  ANYONE,
  SORTS,
  LABELS,
  labelName,
  type Member,
  type Household,
  type Task,
  type Sections,
  type SectionKey,
  type Sort,
  type CalendarView,
} from "./api.js";
import { isSupported, isFragile, listen, type Listener } from "./speech.js";
import {
  status as pushStatus,
  enable as pushEnable,
  disable as pushDisable,
  ensureWorker,
  currentSubscription,
} from "./push.js";

const app = document.getElementById("app")!;
const el = (h: string): HTMLElement => {
  const d = document.createElement("div");
  d.innerHTML = h.trim();
  return d.firstElementChild as HTMLElement;
};
const esc = (s: string) =>
  s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

/**
 * Enter submits. Every one-button card had a mouse-only path to its own primary
 * action, which is wrong on a laptop and worse on a phone keyboard, where the
 * return key is the obvious thing to press and did nothing.
 */
function submitOnEnter(root: HTMLElement, buttonSelector: string): void {
  const button = root.querySelector<HTMLButtonElement>(buttonSelector)!;
  root.querySelectorAll<HTMLInputElement>("input").forEach((f) =>
    f.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        button.click();
      }
    }),
  );
}

// ── state ────────────────────────────────────────────────────────────────────

let me: Member;
let members: Member[] = [];
let household: Household | null = null;
let sections: Sections;

/**
 * Three tabs, matching the three ownership states exactly. There is no fourth
 * "everything" tab: a task that belongs to nobody in particular is a real,
 * different thing from one that belongs to you, and blurring them is what makes
 * shared lists turn into nagging.
 *
 * The tab order IS the ownership axis:
 *
 *     For you  ←──  For all  ──→  You asked
 *
 * "For all" sits in the middle because that is where it sits in reality — one
 * step from yours, one step from theirs. It also makes the tab bar and the
 * swipe agree: swiping left moves a task one tab left, right moves it one tab
 * right, and a gesture that matched the model but not the layout was a small
 * lie the user had to hold in their head.
 */
type Tab = "for_you" | "for_all" | "you_asked";
const TABS: { id: Tab; label: string }[] = [
  { id: "for_you", label: "For you" },
  { id: "for_all", label: "For all" },
  { id: "you_asked", label: "You asked" },
];
let tab: Tab = "for_you";
let composeFor: "partner" | "me" | "all" = "partner";

/** List or calendar. The list is the working view; the calendar is the "when". */
type Mode = "list" | "calendar";
const MODE_KEY = "duly.mode";
let mode: Mode = localStorage.getItem(MODE_KEY) === "calendar" ? "calendar" : "list";
let calendar: CalendarView | null = null;
/** The month the calendar is showing, as YYYY-MM. Null means "this one". */
let calMonth: string | null = null;
/** The day whose agenda is open, as YYYY-MM-DD. */
let calDay: string | null = null;

/**
 * The sort survives a reload, because a preference you have to set again every
 * time is not a preference. Per browser rather than per account: it is a way of
 * looking at the list, not a fact about the household.
 */
const SORT_KEY = "duly.sort";
const SORT_LABELS: Record<Sort, string> = {
  due: "By date",
  added: "Recently added",
  title: "A–Z",
  label: "By label",
};
let sort: Sort = (SORTS as readonly string[]).includes(localStorage.getItem(SORT_KEY) ?? "")
  ? (localStorage.getItem(SORT_KEY) as Sort)
  : "due";

/**
 * The language you SPEAK, which is not the same as the language of the
 * interface — docs/09 §1.4. A German household is perfectly likely to speak
 * English to each other.
 *
 * This lives on the member record and nowhere else. It used to be mirrored in
 * localStorage as well, and the two disagreed: the EN/DE toggle wrote to the
 * copy while the microphone read the record, so pressing EN changed a label and
 * nothing else.
 */
const speechLang = (): string => me?.speechLocale || navigator.language || "en-US";
const setSpeechLang = (v: string): void => {
  me.speechLocale = v;
  members = members.map((m) => (m.id === me.id ? me : m));
  void api.updateMe({ speechLocale: v }).catch(() => {
    /* the mic already uses it; the next boot will re-read the server */
  });
};

const partner = () => members.find((m) => m.id !== me.id) ?? null;
// null is a real owner value now — "either of us" — so every one of these has
// to answer for it rather than crashing on an unexpected null.
const nameOf = (id: string | null) =>
  id === null ? "Either of us" : (members.find((m) => m.id === id)?.displayName ?? "…");
const hueOf = (id: string | null) =>
  id === null ? "var(--mAll)" : members.findIndex((m) => m.id === id) === 0 ? "var(--mA)" : "var(--mB)";
const initial = (id: string | null) => (id === null ? "◇" : (nameOf(id)[0] ?? "?").toUpperCase());

// ── date helpers ─────────────────────────────────────────────────────────────

function whenLabel(t: Task): { text: string; cls: string } {
  if (t.status === "done") return { text: `Done ${short(t.completedAt!)}`, cls: "sage" };
  if (t.status === "declined") return { text: `Declined — ${t.declineReason}`, cls: "" };
  if (t.status === "snoozed") return { text: `Snoozed — ${t.snoozeReason}`, cls: "" };
  if (t.status === "in_progress") return { text: "On it", cls: "sage" };
  if (!t.dueAt) return { text: t.trigger === "arrive_home" ? "When you get home" : "No date", cls: "" };
  const due = new Date(t.dueAt);
  if (due.getTime() < Date.now()) return { text: `Overdue · ${short(t.dueAt)}`, cls: "clay" };
  return { text: short(t.dueAt, t.dueIsDateOnly), cls: "" };
}

function short(iso: string, dateOnly = false): string {
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = dateOnly ? "" : ` ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  if (sameDay) return dateOnly ? "Today" : `Today${time}`;
  return d.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" }) + time;
}

// ── toast with undo ──────────────────────────────────────────────────────────

let toastTimer: number | undefined;
function toast(message: string, undo?: () => void): void {
  document.querySelector(".toast")?.remove();
  clearTimeout(toastTimer);
  const t = el(
    `<div class="toast"><span>${esc(message)}</span>${undo ? "<button>Undo</button>" : ""}</div>`,
  );
  t.querySelector("button")?.addEventListener("click", () => {
    t.remove();
    undo?.();
  });
  document.body.appendChild(t);
  // Ten seconds, per the UX spec — reversibility is what makes one-tap safe.
  toastTimer = window.setTimeout(() => t.remove(), 10_000);
}

// ── onboarding ───────────────────────────────────────────────────────────────

function onboarding(): void {
  app.innerHTML = "";
  const card = el(`
    <div class="center"><div class="card">
      <h2>Say it once.</h2>
      <p>A shared list for two people. No account, no password.</p>
      <input class="field" id="nm" placeholder="Your first name" autocomplete="given-name">
      <div class="err" id="er"></div>
      <button class="btn" id="create">Start a household</button>
      <button class="btn ghost" id="joinBtn">Join with a code</button>
      <button class="btn ghost" id="moveBtn">I already use Duly elsewhere</button>
    </div></div>`);
  app.appendChild(card);

  const nm = card.querySelector<HTMLInputElement>("#nm")!;
  const er = card.querySelector("#er")!;
  nm.focus();

  card.querySelector("#create")!.addEventListener("click", async () => {
    if (!nm.value.trim()) return (er.textContent = "Your name, so your partner knows who asked.");
    try {
      await api.createHousehold("Home", nm.value.trim());
      await boot();
    } catch (e) {
      er.textContent = (e as ApiError).code;
    }
  });

  submitOnEnter(card, "#create");
  card.querySelector("#joinBtn")!.addEventListener("click", () => joinScreen());
  card.querySelector("#moveBtn")!.addEventListener("click", () => transferInScreen());
}

function joinScreen(): void {
  app.innerHTML = "";
  const card = el(`
    <div class="center"><div class="card">
      <h2>Join a household</h2>
      <p>Ask your partner for the six-character code.</p>
      <input class="field" id="code" placeholder="ABC-123" autocapitalize="characters" spellcheck="false">
      <input class="field" id="nm" placeholder="Your first name">
      <div class="err" id="er"></div>
      <button class="btn" id="go">Join</button>
      <button class="btn ghost" id="back">Back</button>
    </div></div>`);
  app.appendChild(card);
  const code = card.querySelector<HTMLInputElement>("#code")!;
  const nm = card.querySelector<HTMLInputElement>("#nm")!;
  const er = card.querySelector("#er")!;
  code.focus();

  card.querySelector("#go")!.addEventListener("click", async () => {
    const c = code.value.replace(/[^A-Za-z0-9]/g, "");
    if (!c || !nm.value.trim()) return (er.textContent = "Both fields, please.");
    try {
      await api.redeem(c, nm.value.trim());
      await boot();
    } catch (e) {
      const err = e as ApiError;
      er.textContent =
        err.code === "unknown_code"
          ? "No household with that code. We skip O, I and L — check for those."
          : err.code === "code_expired"
            ? "That code has expired. Ask for a fresh one."
            : err.code === "code_already_used"
              ? "That code has been used already. Ask for a fresh one."
              : err.code;
    }
  });
  submitOnEnter(card, "#go");
  card.querySelector("#back")!.addEventListener("click", () => onboarding());
}

function invitePanel(): void {
  const scrim = el(`<div class="scrim"><div class="sheet" style="text-align:center">
      <h3>Invite your partner</h3>
      <div class="hint">Single use, expires in 24 hours.</div>
      <div class="code" id="c">······</div>
      <div class="hint" id="u"></div>
      <div class="acts" style="justify-content:center">
        <button class="primary" id="copy">Copy link</button>
        <button id="close">Done</button>
      </div>
    </div></div>`);
  document.body.appendChild(scrim);
  scrim.addEventListener("click", (e) => e.target === scrim && scrim.remove());
  scrim.querySelector("#close")!.addEventListener("click", () => scrim.remove());

  void api.createInvite().then((inv) => {
    scrim.querySelector("#c")!.textContent = `${inv.code.slice(0, 3)}-${inv.code.slice(3)}`;
    scrim.querySelector("#u")!.textContent = inv.url;
    scrim.querySelector("#copy")!.addEventListener("click", async () => {
      await navigator.clipboard.writeText(inv.url);
      toast("Link copied");
    });
  });
}

// ── settings ─────────────────────────────────────────────────────────────────

/**
 * Two groups, and the split is not cosmetic — docs/17. A fact about the home
 * is shared and either of you may change it; a fact about a person is theirs
 * alone. Consent is always personal, which is why the assist level you allow
 * lives in the first group and the household ceiling lives in the second.
 */
const UI_LANGUAGES = [
  { v: "en", label: "English" },
  { v: "de", label: "Deutsch" },
];
const SPEECH_LOCALES = [
  { v: "en-US", label: "English (US)" },
  { v: "en-GB", label: "English (UK)" },
  { v: "de-DE", label: "Deutsch" },
];
const COUNTRIES = [
  { v: "DE", label: "Germany" },
  { v: "AT", label: "Austria" },
  { v: "CH", label: "Switzerland" },
  { v: "GB", label: "United Kingdom" },
  { v: "US", label: "United States" },
];
const ASSIST = [
  { v: "off", label: "Off", sub: "Nothing leaves the device but the words you typed" },
  { v: "basic", label: "Basic", sub: "Dates and names only, on your device" },
  { v: "standard", label: "Standard", sub: "Understands the sentence. Recommended" },
  { v: "full", label: "Full", sub: "Also researches steps, deadlines and what to have ready" },
];

const opts = (list: { v: string; label: string }[], current: string) =>
  list
    .map((o) => `<option value="${o.v}" ${o.v === current ? "selected" : ""}>${esc(o.label)}</option>`)
    .join("");

function settingsPanel(): void {
  const p = partner();
  const h = household!;
  const tz = h.locale?.timezone ?? me.timezone;

  const scrim = el(`<div class="scrim"><div class="sheet settings">
      <h3>Settings</h3>

      <div class="grp">
        <div class="grph">Yours</div>
        <div class="hint">Only you can change these, and your partner never sees them.</div>
        <label class="fld"><span>Your name</span>
          <input id="dn" class="field" value="${esc(me.displayName)}" maxlength="40"></label>
        <label class="fld"><span>App language</span>
          <select id="ui" class="field">${opts(UI_LANGUAGES, me.uiLanguage)}</select></label>
        <label class="fld"><span>You speak</span>
          <select id="sl" class="field">${opts(SPEECH_LOCALES, me.speechLocale)}</select></label>
        <label class="fld"><span>How much help you allow</span>
          <select id="ac" class="field">${opts(ASSIST, me.assistConsent)}</select></label>
        <div class="hint" id="acs">${esc(ASSIST.find((a) => a.v === me.assistConsent)?.sub ?? "")}</div>
      </div>

      <div class="grp">
        <div class="grph">Both of you</div>
        <div class="hint">Either of you can change these, and they change for both.</div>
        <label class="fld"><span>Home name</span>
          <input id="hn" class="field" value="${esc(h.name)}" maxlength="60"></label>
        <label class="fld"><span>Country</span>
          <select id="cc" class="field">${opts(COUNTRIES, h.locale?.country ?? "DE")}</select></label>
        <div class="hint">Sets currency, holidays and how notice periods are read — a German gym contract is not an American one.</div>
        <label class="fld"><span>Time zone</span>
          <input id="tz" class="field" value="${esc(tz)}" readonly></label>
        <label class="fld"><span>Help ceiling for the home</span>
          <select id="hl" class="field">${opts(ASSIST, h.assist?.level ?? "standard")}</select></label>
        <div class="hint">A ceiling, never a floor: it can lower what either of you allows, never raise it.</div>
      </div>

      <div class="grp">
        <div class="grph">Notifications</div>
        <div id="pushbox"><div class="hint">Checking…</div></div>
      </div>

      <div class="grp">
        <div class="grph">${p ? "Your household" : "Your partner"}</div>
        ${
          p
            ? `<div class="hint">Paired with ${esc(p.displayName)}.</div>`
            : `<button class="btn ghost" id="invite">Invite your partner</button>`
        }
        <button class="btn ghost" id="movedev">Use Duly on another device</button>
        <button class="btn ghost danger" id="out">Sign out</button>
      </div>

      <div class="acts"><button id="close">Done</button></div>
    </div></div>`);
  document.body.appendChild(scrim);

  const q = <T extends HTMLElement>(s: string) => scrim.querySelector<T>(s)!;
  const close = (): void => scrim.remove();
  scrim.addEventListener("click", (e) => e.target === scrim && close());
  q("#close").addEventListener("click", close);

  const saveMe = async (patch: Record<string, unknown>): Promise<void> => {
    try {
      me = (await api.updateMe(patch)).member;
      members = members.map((m) => (m.id === me.id ? me : m));
      toast("Saved");
    } catch (e) {
      toast(`Not saved — ${(e as ApiError).code}`);
    }
  };
  const saveHome = async (patch: Record<string, unknown>): Promise<void> => {
    try {
      household = (await api.updateHousehold(patch)).household;
      toast("Saved for both of you");
    } catch (e) {
      toast(`Not saved — ${(e as ApiError).code}`);
    }
  };

  q<HTMLInputElement>("#dn").addEventListener("change", (e) =>
    void saveMe({ displayName: (e.target as HTMLInputElement).value.trim() }),
  );
  q<HTMLSelectElement>("#ui").addEventListener("change", (e) =>
    void saveMe({ uiLanguage: (e.target as HTMLSelectElement).value }),
  );
  q<HTMLSelectElement>("#sl").addEventListener("change", (e) => {
    const v = (e.target as HTMLSelectElement).value;
    setSpeechLang(v); // the mic reads this immediately
    void saveMe({ speechLocale: v });
  });
  q<HTMLSelectElement>("#ac").addEventListener("change", (e) => {
    const v = (e.target as HTMLSelectElement).value;
    q("#acs").textContent = ASSIST.find((a) => a.v === v)?.sub ?? "";
    void saveMe({ assistConsent: v });
  });

  q<HTMLInputElement>("#hn").addEventListener("change", (e) =>
    void saveHome({ name: (e.target as HTMLInputElement).value.trim() }),
  );
  q<HTMLSelectElement>("#cc").addEventListener("change", (e) =>
    void saveHome({ country: (e.target as HTMLSelectElement).value }),
  );
  q<HTMLSelectElement>("#hl").addEventListener("change", (e) =>
    void saveHome({ assistLevel: (e.target as HTMLSelectElement).value }),
  );

  void renderPush(q("#pushbox"));

  scrim.querySelector("#invite")?.addEventListener("click", () => {
    close();
    invitePanel();
  });
  q("#movedev").addEventListener("click", () => {
    close();
    transferPanel();
  });
  q("#out").addEventListener("click", () => {
    close();
    signOutPanel();
  });
}

/**
 * The notification section of settings.
 *
 * Written as a state machine because there are six honest answers and only one
 * of them is a toggle. The temptation is to show a switch that silently does
 * nothing in the other five; that is how you get a person who thinks
 * notifications are on and quietly stops trusting the app.
 */
async function renderPush(host: HTMLElement): Promise<void> {
  const key = await api.pushKey().catch(() => ({ enabled: false, publicKey: null }));
  const s = await pushStatus(key.enabled);

  const paint = (inner: string): HTMLElement => {
    host.innerHTML = "";
    const box = el(`<div>${inner}</div>`);
    host.appendChild(box);
    return box;
  };

  if (s.state === "needs_install") {
    // The single most confusing case, and the one worth spelling out: on iOS
    // Safari refuses notifications from a tab entirely. It is not broken and
    // it is not a setting — the site has to be on the Home Screen.
    paint(`<div class="hint">
        iPhone and iPad only allow notifications once Duly is on your Home Screen.
        Tap <b>Share</b> then <b>Add to Home Screen</b>, open it from there, and
        this setting will appear.
      </div>`);
    return;
  }
  if (s.state === "unsupported") {
    paint(`<div class="hint">This browser cannot show notifications. Chrome, Edge,
      Firefox and Safari 16.4 or later all can.</div>`);
    return;
  }
  if (s.state === "not_configured") {
    paint(`<div class="hint">Notifications are not switched on for this server yet.</div>`);
    return;
  }
  if (s.state === "denied") {
    // Only the user can undo this, and the browser will not ask again.
    paint(`<div class="hint">You blocked notifications for Duly. To change it, open the
      padlock or ⓘ next to the address bar and allow notifications there.</div>`);
    return;
  }

  const on = s.state === "on";
  const box = paint(`
    <button class="btn ghost" id="toggle">${on ? "Turn notifications off" : "Turn notifications on"}</button>
    <div class="hint">${
      on
        ? "Quiet hours are respected, and Duly limits itself to two of its own reminders a day. Anything your partner does — asking, finishing, thanking — always comes through."
        : "You will hear when your partner asks for something, finishes something, or says thanks — plus a short morning and evening summary of your own."
    }</div>`);

  box.querySelector("#toggle")!.addEventListener("click", async () => {
    const btn = box.querySelector<HTMLButtonElement>("#toggle")!;
    btn.disabled = true;
    btn.textContent = on ? "Turning off…" : "Asking…";

    if (on) {
      const endpoint = await pushDisable();
      if (endpoint) await api.pushUnsubscribe(endpoint).catch(() => {});
      toast("Notifications off");
    } else {
      const res = await pushEnable(key.publicKey!);
      if (!res.ok) {
        toast(
          res.reason === "denied"
            ? "Blocked — allow notifications in the address bar"
            : res.reason === "dismissed"
              ? "No problem — you can turn it on any time"
              : `Couldn't turn it on — ${res.detail ?? "unknown"}`,
        );
        await renderPush(host);
        return;
      }
      const sub = await currentSubscription();
      if (sub) {
        try {
          await api.pushSubscribe(sub);
          toast("Notifications on");
        } catch (e) {
          // Registered with the browser but not with us: unsubscribe again
          // rather than leaving a device that thinks it is subscribed and
          // never hears anything.
          await pushDisable();
          toast(`Couldn't register — ${(e as ApiError).code}`);
        }
      }
    }
    await renderPush(host);
  });
}

/** A code that carries this account to another browser or phone. */
function transferPanel(): void {
  const scrim = el(`<div class="scrim"><div class="sheet" style="text-align:center">
      <h3>Use Duly on another device</h3>
      <div class="hint">Open Duly there and enter this code. Single use, valid 15 minutes.</div>
      <div class="code" id="c">······</div>
      <div class="hint">This does not add a second person — it is the same account in two places.</div>
      <div class="acts" style="justify-content:center"><button id="close">Done</button></div>
    </div></div>`);
  document.body.appendChild(scrim);
  scrim.addEventListener("click", (e) => e.target === scrim && scrim.remove());
  scrim.querySelector("#close")!.addEventListener("click", () => scrim.remove());
  void api
    .transferCode()
    .then((r) => {
      scrim.querySelector("#c")!.textContent = `${r.code.slice(0, 3)}-${r.code.slice(3)}`;
    })
    .catch((e: ApiError) => {
      scrim.querySelector("#c")!.textContent = "—";
      toast(`Couldn't make a code — ${e.code}`);
    });
}

/**
 * Signing out clears the only credential that exists. There is no password to
 * come back with, so the way back is offered BEFORE the door closes rather
 * than explained after it.
 */
function signOutPanel(): void {
  const scrim = el(`<div class="scrim"><div class="sheet">
      <h3>Sign out?</h3>
      <p class="hint">Duly has no password. Once you sign out, this browser gets back in
      only with a code — either a transfer code you make now, or a fresh invite from
      your partner.</p>
      <div class="code" id="c">Getting a code…</div>
      <div class="hint">Write it down first. It is valid for 15 minutes.</div>
      <div class="acts">
        <button id="cancel">Stay signed in</button>
        <button class="danger" id="go" disabled>Sign out</button>
      </div>
    </div></div>`);
  document.body.appendChild(scrim);
  scrim.addEventListener("click", (e) => e.target === scrim && scrim.remove());
  scrim.querySelector("#cancel")!.addEventListener("click", () => scrim.remove());

  const go = scrim.querySelector<HTMLButtonElement>("#go")!;
  void api
    .transferCode()
    .then((r) => {
      scrim.querySelector("#c")!.textContent = `${r.code.slice(0, 3)}-${r.code.slice(3)}`;
      go.disabled = false;
    })
    .catch(() => {
      // Still allow it — refusing to sign out because a code could not be
      // minted would be worse — but say plainly what it costs.
      scrim.querySelector("#c")!.textContent = "No code available";
      scrim.querySelector(".hint:last-of-type")!.textContent =
        "You will need an invite from your partner to get back in.";
      go.disabled = false;
    });

  go.addEventListener("click", () => {
    // Unregister push BEFORE dropping the token: afterwards there is no
    // credential left to authenticate the call, and the server would keep
    // sending this device notifications for an account it can no longer open.
    void pushDisable()
      .then((endpoint) => (endpoint ? api.pushUnsubscribe(endpoint) : undefined))
      .catch(() => {})
      .finally(() => {
        token.clear();
        localStorage.removeItem(SORT_KEY);
        localStorage.removeItem(MODE_KEY);
        location.replace("/");
      });
  });
}

/** The other half: arriving on a new device with a transfer code. */
function transferInScreen(): void {
  app.innerHTML = "";
  const card = el(`
    <div class="center"><div class="card">
      <h2>Move your account here</h2>
      <p>Enter the transfer code from your other device.</p>
      <input class="field" id="code" placeholder="ABC-123" autocapitalize="characters" spellcheck="false">
      <div class="err" id="er"></div>
      <button class="btn" id="go">Continue</button>
      <button class="btn ghost" id="back">Back</button>
    </div></div>`);
  app.appendChild(card);
  const code = card.querySelector<HTMLInputElement>("#code")!;
  const er = card.querySelector("#er")!;
  code.focus();

  card.querySelector("#go")!.addEventListener("click", async () => {
    const c = code.value.replace(/[^A-Za-z0-9]/g, "");
    if (!c) return (er.textContent = "The code from your other device.");
    try {
      const r = await api.transferIn(c);
      token.set(r.token);
      await boot();
    } catch (e) {
      er.textContent =
        (e as ApiError).code === "unknown_code"
          ? "That code is not valid any more. Make a fresh one on the other device."
          : (e as ApiError).code;
    }
  });
  submitOnEnter(card, "#go");
  card.querySelector("#back")!.addEventListener("click", () => onboarding());
}

// ── dashboard ────────────────────────────────────────────────────────────────

const SECTION_TITLES: Record<string, string> = {
  needs_you: "Needs you",
  waiting_on: "Waiting on",
  for_all: "Either of us",
  coming_up: "Coming up",
  no_date: "No date",
  done_today: "Done today",
};

/** Which sections each tab shows. The server groups; the tab only chooses. */
const TAB_SECTIONS: Record<Tab, SectionKey[]> = {
  for_you: ["needs_you", "coming_up", "no_date", "done_today"],
  for_all: ["for_all"],
  you_asked: ["waiting_on"],
};

const EMPTY_LINE: Record<Tab, string> = {
  for_you: "Nothing overdue, nothing waiting.",
  for_all: "Nothing sitting in the middle.",
  you_asked: "Nothing outstanding from you.",
};

/**
 * When sorting by label, the label becomes the heading and the sections stay
 * as the outer grouping. Sorting alone would have produced a correctly ordered
 * list with no visible grouping — the order would be right and invisible.
 */
function labelRuns(list: Task[]): { label: string | null; items: Task[] }[] {
  const runs: { label: string | null; items: Task[] }[] = [];
  for (const t of list) {
    const key = t.category ?? null;
    const last = runs[runs.length - 1];
    if (last && last.label === key) last.items.push(t);
    else runs.push({ label: key, items: [t] });
  }
  return runs;
}

function render(): void {
  app.innerHTML = "";
  const wrap = el(`<div class="wrap"></div>`);

  const p = partner();
  wrap.appendChild(
    el(`<header class="top">
      <h1>${esc(household?.name ?? "Home")}</h1>
      <div class="who">
        ${members
          .map(
            (m) =>
              `<span class="av" style="background:${hueOf(m.id)}" title="${esc(m.displayName)}">${esc(initial(m.id))}</span>`,
          )
          .join("")}
        <button class="iconbtn" id="mode" title="${mode === "list" ? "Calendar" : "List"}"
          aria-label="${mode === "list" ? "Show the calendar" : "Show the list"}">${mode === "list" ? "▦" : "☰"}</button>
        <button class="iconbtn" id="settings" title="Settings" aria-label="Settings">⚙</button>
      </div>
    </header>`),
  );

  wrap.querySelector("#mode")!.addEventListener("click", () => {
    mode = mode === "list" ? "calendar" : "list";
    localStorage.setItem(MODE_KEY, mode);
    void refresh();
  });
  wrap.querySelector("#settings")!.addEventListener("click", () => settingsPanel());

  if (mode === "calendar") {
    renderCalendar(wrap);
    app.appendChild(wrap);
    app.appendChild(composer());
    return;
  }

  const counts: Record<Tab, number> = {
    for_you: TAB_SECTIONS.for_you.filter((k) => k !== "done_today")
      .reduce((n, k) => n + (sections[k]?.length ?? 0), 0),
    for_all: sections.for_all?.length ?? 0,
    you_asked: sections.waiting_on?.length ?? 0,
  };

  const tabs = el(`<div class="tabs" role="tablist">
      ${TABS.map(
        (t) =>
          `<button role="tab" data-t="${t.id}" aria-selected="${tab === t.id}">${esc(t.label)}${
            counts[t.id] ? `<i>${counts[t.id]}</i>` : ""
          }</button>`,
      ).join("")}
    </div>`);
  wrap.appendChild(tabs);

  // The sort control names the CURRENT order rather than the word "Sort", so
  // the list never has to be decoded — you can see how it is ordered without
  // opening anything.
  const sorter = el(`<div class="sortbar">
      <button id="sortbtn" aria-haspopup="true">${esc(SORT_LABELS[sort])} ⌄</button>
    </div>`);
  wrap.appendChild(sorter);

  let shown = 0;
  for (const k of TAB_SECTIONS[tab]) {
    const list = sections[k] ?? [];
    if (!list.length) continue;
    shown += list.length;
    const title = k === "waiting_on" && p ? `Waiting on ${p.displayName}` : SECTION_TITLES[k];
    const sec = el(`<section class="sec"><h2>${esc(title!)} <i>${list.length}</i></h2></section>`);
    if (sort === "label") {
      // The label becomes a visible sub-heading. Ordering without grouping puts
      // the tasks in the right order and shows you nothing.
      for (const run of labelRuns(list)) {
        sec.appendChild(
          el(`<h3 class="lrun">${esc(labelName(run.label) || "No label")}</h3>`),
        );
        for (const t of run.items) sec.appendChild(taskRow(t));
      }
    } else {
      for (const t of list) sec.appendChild(taskRow(t));
    }
    wrap.appendChild(sec);
  }

  // The empty state is the goal state, not a blank screen.
  if (!shown) {
    wrap.appendChild(
      el(`<div class="clear"><div class="glow"></div>
        <div class="big">All clear.</div>
        <div class="sub">${esc(EMPTY_LINE[tab])}</div>
      </div>`),
    );
  }

  app.appendChild(wrap);
  app.appendChild(composer());

  tabs.querySelectorAll("button").forEach((b) =>
    b.addEventListener("click", () => {
      tab = b.dataset["t"] as Tab;
      render();
    }),
  );
  sorter.querySelector("#sortbtn")!.addEventListener("click", () => sortMenu());
}

// ── calendar ─────────────────────────────────────────────────────────────────

const MONTH_FMT = new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" });
/** Monday first. Every country Duly currently knows starts the week on Monday. */
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** YYYY-MM-DD arithmetic that never touches a timezone. */
const ymd = (y: number, m: number, d: number) =>
  `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

function renderCalendar(wrap: HTMLElement): void {
  const cal = calendar;
  if (!cal) {
    wrap.appendChild(el(`<div class="clear"><div class="sub">Loading…</div></div>`));
    return;
  }

  const month = calMonth ?? cal.today.slice(0, 7);
  const [yy, mm] = month.split("-").map(Number) as [number, number];
  // Day 0 of the next month is the last day of this one.
  const daysInMonth = new Date(Date.UTC(yy, mm, 0)).getUTCDate();
  // getUTCDay is 0=Sunday; shift so Monday is 0.
  const lead = (new Date(Date.UTC(yy, mm - 1, 1)).getUTCDay() + 6) % 7;

  const head = el(`<div class="calhead">
      <button id="prev" aria-label="Previous month">‹</button>
      <h2>${esc(MONTH_FMT.format(new Date(Date.UTC(yy, mm - 1, 1))))}</h2>
      <button id="next" aria-label="Next month">›</button>
    </div>`);
  wrap.appendChild(head);

  const grid = el(`<div class="calgrid">
      ${WEEKDAYS.map((d) => `<div class="cdow">${d}</div>`).join("")}
    </div>`);

  for (let i = 0; i < lead; i++) grid.appendChild(el(`<div class="cell blank"></div>`));

  for (let d = 1; d <= daysInMonth; d++) {
    const key = ymd(yy, mm, d);
    const items = cal.days[key] ?? [];
    const open = items.filter((t) => t.status !== "done");
    const overdue = open.some((t) => t.dueAt && Date.parse(t.dueAt) < Date.now());
    const cell = el(`<button class="cell ${key === cal.today ? "today" : ""} ${
      key === calDay ? "picked" : ""
    }" data-d="${key}" aria-label="${esc(key)}, ${open.length} open">
        <span class="n">${d}</span>
        ${
          items.length
            ? `<span class="dots">${items
                .slice(0, 4)
                .map(
                  (t) =>
                    `<i class="dot ${t.status === "done" ? "done" : ""}" style="background:${
                      t.status === "done" ? "var(--sage)" : hueOf(t.ownerId)
                    }"></i>`,
                )
                .join("")}${items.length > 4 ? `<i class="more">+</i>` : ""}</span>`
            : ""
        }
        ${overdue ? `<span class="odue"></span>` : ""}
      </button>`);
    grid.appendChild(cell);
  }
  wrap.appendChild(grid);

  head.querySelector("#prev")!.addEventListener("click", () => {
    calMonth = mm === 1 ? `${yy - 1}-12` : ymd(yy, mm - 1, 1).slice(0, 7);
    calDay = null;
    render();
  });
  head.querySelector("#next")!.addEventListener("click", () => {
    calMonth = mm === 12 ? `${yy + 1}-01` : ymd(yy, mm + 1, 1).slice(0, 7);
    calDay = null;
    render();
  });
  grid.querySelectorAll<HTMLButtonElement>(".cell[data-d]").forEach((c) =>
    c.addEventListener("click", () => {
      // Tapping the open day closes it, so the grid can be seen whole again.
      calDay = calDay === c.dataset["d"] ? null : c.dataset["d"]!;
      render();
    }),
  );

  // The agenda: the picked day, or — when nothing is picked — everything still
  // ahead. A month grid alone answers "when", never "what".
  const agenda = el(`<div class="agenda"></div>`);
  if (calDay) {
    const items = cal.days[calDay] ?? [];
    agenda.appendChild(el(`<h2 class="ah">${esc(longDay(calDay))} <i>${items.length}</i></h2>`));
    if (!items.length) agenda.appendChild(el(`<div class="hint">Nothing on this day.</div>`));
    for (const t of items) agenda.appendChild(taskRow(t));
  } else {
    const upcoming = Object.keys(cal.days)
      .filter((k) => k >= cal.today)
      .sort()
      .slice(0, 14);
    agenda.appendChild(el(`<h2 class="ah">Coming up</h2>`));
    if (!upcoming.length) agenda.appendChild(el(`<div class="hint">Nothing dated ahead.</div>`));
    for (const k of upcoming) {
      agenda.appendChild(el(`<h3 class="lrun">${esc(longDay(k))}</h3>`));
      for (const t of cal.days[k]!) agenda.appendChild(taskRow(t));
    }
  }

  // Undated tasks are named rather than hidden. A calendar that quietly drops
  // half the list is one people stop trusting.
  if (cal.undated.length) {
    const n = cal.undated.length;
    const box = el(`<details class="undated">
        <summary>${n} ${n === 1 ? "task has" : "tasks have"} no date</summary>
      </details>`);
    for (const t of cal.undated) box.appendChild(taskRow(t));
    agenda.appendChild(box);
  }
  wrap.appendChild(agenda);
}

function longDay(key: string): string {
  const [y, m, d] = key.split("-").map(Number) as [number, number, number];
  // Built in UTC from the parts, so the label can never slide onto the
  // neighbouring day in a browser running a different timezone.
  return new Intl.DateTimeFormat(undefined, {
    weekday: "long", day: "numeric", month: "long", timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

function sortMenu(): void {
  const scrim = el(`<div class="scrim"><div class="sheet">
      <h3>Sort</h3>
      <div class="menu">
        ${SORTS.map(
          (s) =>
            `<button data-s="${s}" aria-pressed="${s === sort}">${esc(SORT_LABELS[s])}${
              s === sort ? " ✓" : ""
            }</button>`,
        ).join("")}
      </div>
      <div class="hint">Sorting reorders each group. The groups themselves stay put.</div>
    </div></div>`);
  document.body.appendChild(scrim);
  scrim.addEventListener("click", (e) => e.target === scrim && scrim.remove());
  scrim.querySelectorAll<HTMLButtonElement>(".menu button").forEach((b) =>
    b.addEventListener("click", async () => {
      sort = b.dataset["s"] as Sort;
      localStorage.setItem(SORT_KEY, sort);
      scrim.remove();
      await refresh();
    }),
  );
}

/**
 * Ownership is one axis with three stops, and moving a task is a step along it:
 *
 *     you  ←→  either of us  ←→  them
 *
 * A swipe takes one step. Left pulls a task toward you, right pushes it away —
 * which is the only mapping most people guess right, and it makes the gesture
 * its own undo. The three tabs sit in the order the user asked for rather than
 * this one, so the destination is always named out loud in the toast.
 */
function axis(): (string | null)[] {
  const p = partner();
  return p ? [me.id, null, p.id] : [me.id, null];
}

function stepOwner(current: string | null, dir: -1 | 1): string | null | undefined {
  const a = axis();
  const i = a.findIndex((x) => x === current);
  const next = i + dir;
  if (i < 0 || next < 0 || next >= a.length) return undefined;
  return a[next];
}

/** One place that turns an owner into the value the server wants. */
const ownerParam = (o: string | null) => (o === null ? ANYONE : o);

async function moveTo(t: Task, to: string | null): Promise<void> {
  const from = t.ownerId;
  try {
    await api.assign(t._id, ownerParam(to));
  } catch (e) {
    toast(`Couldn't move it — ${(e as ApiError).code}`);
    return;
  }
  toast(
    to === null
      ? "Now either of us"
      : to === me.id
        ? "Moved to you"
        : `Moved to ${nameOf(to)}`,
    async () => {
      await api.assign(t._id, ownerParam(from));
      await refresh();
    },
  );
  await refresh();
}

function taskRow(t: Task): HTMLElement {
  const w = whenLabel(t);
  const overdue = w.cls === "clay";
  const showAvatar = t.ownerId !== me.id;
  const fromAI = Object.values(t.aiFieldOrigin ?? {}).some((v) => v !== "user");
  const mine = t.ownerId === me.id || t.ownerId === null;
  // Under a label heading the chip repeats what the heading just said.
  const showLabel = !!t.category && !(mode === "list" && sort === "label");

  const row = el(`<article class="row ${overdue ? "overdue" : ""} ${t.status === "snoozed" ? "snoozed" : ""} ${t.status === "done" ? "done" : ""}">
      <button class="ck ${t.status === "done" ? "done" : ""}" aria-label="Mark ${esc(t.title)} as done"></button>
      <div class="body">
        <div class="tt">${esc(t.title)}</div>
        <div class="mt ${w.cls}">${esc(w.text)}${t.ownerId !== t.requesterId && t.ownerId !== null ? ` · asked by ${esc(nameOf(t.requesterId))}` : ""}${t.notes ? " · note" : ""}</div>
        ${showLabel || fromAI ? `<div class="chips">${showLabel ? `<span class="chip">${esc(labelName(t.category))}</span>` : ""}${fromAI ? `<span class="chip ai">understood</span>` : ""}</div>` : ""}
      </div>
      <button class="movebtn" title="Move" aria-label="Move ${esc(t.title)} to someone else">⇄</button>
      ${showAvatar ? `<span class="av" style="background:${hueOf(t.ownerId)}">${esc(initial(t.ownerId))}</span>` : ""}
    </article>`);

  row.querySelector(".body")!.addEventListener("click", () => detail(t._id));

  // The visible button is the real control; the swipe below is a shortcut for
  // people who find it. A gesture nobody knows about is not a feature.
  row.querySelector(".movebtn")!.addEventListener("click", (e) => {
    e.stopPropagation();
    movePanel(t);
  });

  row.querySelector(".ck")!.addEventListener("click", async (e) => {
    e.stopPropagation();
    if (!mine) return toast("Only " + nameOf(t.ownerId) + " can finish this");
    const done = t.status === "done";
    row.classList.add("leaving");
    await api.act(t._id, done ? "reopen" : "complete");
    toast(done ? "Reopened" : "Done", async () => {
      await api.act(t._id, done ? "complete" : "reopen");
      await refresh();
    });
    await refresh();
  });

  if (t.status !== "done") attachSwipe(row, t);
  return row;
}

/**
 * Horizontal drag to move a task one stop along the ownership axis.
 *
 * Only takes over once the drag is clearly sideways, so a vertical scroll
 * through the list is never hijacked — the usual reason swipe rows feel broken
 * on a phone.
 */
function attachSwipe(row: HTMLElement, t: Task): void {
  const THRESHOLD = 72;
  let x0 = 0;
  let y0 = 0;
  let dragging = false;
  let decided = false;

  row.addEventListener("pointerdown", (e) => {
    if ((e.target as HTMLElement).closest("button")) return;
    x0 = e.clientX;
    y0 = e.clientY;
    dragging = true;
    decided = false;
  });

  row.addEventListener("pointermove", (e) => {
    if (!dragging) return;
    const dx = e.clientX - x0;
    const dy = e.clientY - y0;
    if (!decided) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      if (Math.abs(dy) > Math.abs(dx)) return (dragging = false); // a scroll
      decided = true;
      row.setPointerCapture(e.pointerId);
      row.classList.add("dragging");
    }
    const dir = dx < 0 ? -1 : 1;
    const target = stepOwner(t.ownerId, dir as -1 | 1);
    // Rubber-band when there is nowhere to go, rather than promising a move
    // that will not happen.
    const capped = target === undefined ? dx * 0.25 : dx;
    row.style.transform = `translateX(${capped}px)`;
    row.classList.toggle("armed", target !== undefined && Math.abs(dx) > THRESHOLD);
    row.dataset["hint"] =
      target === undefined ? "" : target === null ? "Either of us" : nameOf(target);
  });

  const end = async (e: PointerEvent): Promise<void> => {
    if (!dragging) return;
    dragging = false;
    const dx = e.clientX - x0;
    row.classList.remove("dragging", "armed");
    row.style.transform = "";
    if (!decided || Math.abs(dx) <= THRESHOLD) return;
    const target = stepOwner(t.ownerId, dx < 0 ? -1 : 1);
    if (target === undefined) return;
    await moveTo(t, target);
  };
  row.addEventListener("pointerup", (e) => void end(e));
  row.addEventListener("pointercancel", (e) => void end(e));
}

// ── when: a date, and optionally a time ──────────────────────────────────────

function whenRowLabel(t: Task): string {
  if (!t.dueAt) return "No date";
  return t.dueIsDateOnly ? `${short(t.dueAt, true)} · all day` : short(t.dueAt);
}

/** The local YYYY-MM-DD and HH:MM of an instant, for the two native inputs. */
function fieldsFor(iso: string | null): { date: string; time: string } {
  if (!iso) return { date: "", time: "" };
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

/** The browser's own offset at that local moment, as +HH:MM. */
function offsetAt(date: string, time: string): string {
  const local = new Date(`${date}T${time}:00`);
  const mins = -local.getTimezoneOffset();
  const sign = mins < 0 ? "-" : "+";
  const a = Math.abs(mins);
  return `${sign}${String(Math.floor(a / 60)).padStart(2, "0")}:${String(a % 60).padStart(2, "0")}`;
}

/**
 * Date and time are two fields, not one.
 *
 * "Friday" and "Friday at 14:30" are genuinely different statements — the first
 * says which day, the second makes an appointment — and a single datetime input
 * forces a time on every task that only ever had a day. So the time is optional
 * and empty means all-day, which is exactly what `dueIsDateOnly` already meant
 * on the server.
 */
function whenPanel(t: Task): void {
  const f = fieldsFor(t.dueAt);
  const timeShown = t.dueAt && !t.dueIsDateOnly ? f.time : "";

  const scrim = el(`<div class="scrim"><div class="sheet">
      <h3>When?</h3>
      <div class="whenrow">
        <label class="fld"><span>Date</span>
          <input type="date" id="d" class="field" value="${f.date}"></label>
        <label class="fld"><span>Time <em>optional</em></span>
          <input type="time" id="tm" class="field" value="${timeShown}" step="300"></label>
      </div>
      <div class="hint">Leave the time empty for an all-day task. With a time it becomes an appointment, and reminders can fire before it.</div>
      <div class="quick" id="quick"></div>
      <div class="acts">
        <button class="primary" id="save">Save</button>
        ${t.dueAt ? `<button id="clear">Remove the date</button>` : ""}
        <button id="cancel">Cancel</button>
      </div>
    </div></div>`);
  document.body.appendChild(scrim);
  scrim.addEventListener("click", (e) => e.target === scrim && scrim.remove());
  scrim.querySelector("#cancel")!.addEventListener("click", () => scrim.remove());

  const dEl = scrim.querySelector<HTMLInputElement>("#d")!;
  const tEl = scrim.querySelector<HTMLInputElement>("#tm")!;

  // Shortcuts for the dates people actually pick, so the common case is one tap
  // rather than a date picker.
  const now = new Date();
  const plus = (n: number): string => {
    const d = new Date(now);
    d.setDate(d.getDate() + n);
    return fieldsFor(d.toISOString()).date;
  };
  const quick: { label: string; date: string; time?: string }[] = [
    { label: "Today", date: plus(0) },
    { label: "Tomorrow", date: plus(1) },
    { label: "This weekend", date: plus((6 - now.getDay() + 7) % 7 || 7) },
    { label: "Next week", date: plus(7) },
  ];
  const quickHost = scrim.querySelector("#quick")!;
  for (const q of quick) {
    const b = el(`<button class="chip tap">${esc(q.label)}</button>`);
    b.addEventListener("click", () => {
      dEl.value = q.date;
      if (q.time) tEl.value = q.time;
    });
    quickHost.appendChild(b);
  }

  const commit = async (dueAt: string | null): Promise<void> => {
    try {
      await api.patch(t._id, { dueAt });
      scrim.remove();
      await refresh();
    } catch (e) {
      toast(`Not saved — ${(e as ApiError).code}`);
    }
  };

  scrim.querySelector("#save")!.addEventListener("click", () => {
    if (!dEl.value) return toast("Pick a date, or remove it entirely");
    // A bare date tells the server "all day"; a full ISO instant tells it there
    // is a real clock time. That is the same contract the parser uses.
    void commit(
      tEl.value ? `${dEl.value}T${tEl.value}:00${offsetAt(dEl.value, tEl.value)}` : dEl.value,
    );
  });
  scrim.querySelector("#clear")?.addEventListener("click", () => void commit(null));
}

// ── labels ───────────────────────────────────────────────────────────────────

/**
 * Six labels, no free text. Free-text labels drift — "shopping", "Shopping",
 * "groceries" and "einkaufen" become four labels for one idea in a week, and
 * then nothing groups. Six fixed ones can also be assigned by the parser
 * without asking, which is the only way a label ever lands on a task spoken in
 * four seconds.
 */
function labelPanel(t: Task): void {
  const scrim = el(`<div class="scrim"><div class="sheet">
      <h3>Label</h3>
      <div class="menu">
        ${LABELS.map(
          (l) =>
            `<button data-l="${l.id}" ${l.id === t.category ? "disabled" : ""}>
               <b>${esc(l.name)}</b><span>${esc(l.sub)}</span>
               ${l.id === t.category ? "<em>current</em>" : ""}
             </button>`,
        ).join("")}
        ${t.category ? `<button data-l=""><b>No label</b><span>Take the label off</span></button>` : ""}
      </div>
      <div class="hint">Labels group the list when you sort by label. Duly guesses one when it understands the task.</div>
    </div></div>`);
  document.body.appendChild(scrim);
  scrim.addEventListener("click", (e) => e.target === scrim && scrim.remove());
  scrim.querySelectorAll<HTMLButtonElement>(".menu button").forEach((b) =>
    b.addEventListener("click", async () => {
      const next = b.dataset["l"] || null;
      scrim.remove();
      try {
        await api.patch(t._id, { category: next });
        await refresh();
      } catch (e) {
        toast(`Not saved — ${(e as ApiError).code}`);
      }
    }),
  );
}

/** The explicit route: every destination named, one tap each. */
function movePanel(t: Task): void {
  const p = partner();
  const options: { id: string; label: string; sub: string; owner: string | null }[] = [
    { id: "me", label: "For you", sub: "Yours to do", owner: me.id },
    { id: "all", label: "For all", sub: "Either of us can pick it up", owner: null },
  ];
  if (p) {
    options.splice(1, 0, {
      id: "them",
      label: `For ${p.displayName}`,
      sub: "You are asking, they are doing",
      owner: p.id,
    });
  }

  const scrim = el(`<div class="scrim"><div class="sheet">
      <h3>Move “${esc(t.title)}”</h3>
      <div class="menu">
        ${options
          .map(
            (o) =>
              `<button data-o="${o.id}" ${o.owner === t.ownerId ? "disabled" : ""}>
                 <b>${esc(o.label)}</b><span>${esc(o.sub)}</span>
                 ${o.owner === t.ownerId ? "<em>where it is now</em>" : ""}
               </button>`,
          )
          .join("")}
      </div>
      <div class="hint">You can also swipe a task sideways.</div>
    </div></div>`);
  document.body.appendChild(scrim);
  scrim.addEventListener("click", (e) => e.target === scrim && scrim.remove());
  scrim.querySelectorAll<HTMLButtonElement>(".menu button").forEach((b) =>
    b.addEventListener("click", async () => {
      const o = options.find((x) => x.id === b.dataset["o"])!;
      scrim.remove();
      await moveTo(t, o.owner);
    }),
  );
}

// ── composer ─────────────────────────────────────────────────────────────────

function composer(): HTMLElement {
  const p = partner();
  const c = el(`<div class="composer"><div class="inner">
      ${isSupported() ? `<button class="mic" id="mic" title="Speak" aria-label="Speak a task"><span class="micglyph"></span></button>` : ""}
      <input id="say" placeholder="${p ? `Ask ${esc(p.displayName)}, or add for yourself…` : "Add something…"}" autocomplete="off">
      <button class="go" id="go" disabled>→</button>
    </div>
    <div class="forwho">
      ${p ? `<button data-w="partner" aria-pressed="${composeFor === "partner"}">For ${esc(p.displayName)}</button>
      <button data-w="me" aria-pressed="${composeFor === "me"}">For me</button>
      <button data-w="all" aria-pressed="${composeFor === "all"}">For all</button>` : ""}
      ${isSupported() ? `<span class="sep"></span>
      <button data-l="en-US" aria-pressed="${speechLang().startsWith("en")}" title="Speak English">EN</button>
      <button data-l="de-DE" aria-pressed="${speechLang().startsWith("de")}" title="Speak German">DE</button>` : ""}
    </div></div>`);

  const input = c.querySelector<HTMLInputElement>("#say")!;
  const go = c.querySelector<HTMLButtonElement>("#go")!;
  input.addEventListener("input", () => (go.disabled = !input.value.trim()));
  c.querySelectorAll<HTMLButtonElement>(".forwho button").forEach((b) =>
    b.addEventListener("click", () => {
      if (b.dataset["w"]) composeFor = b.dataset["w"] as typeof composeFor;
      if (b.dataset["l"]) setSpeechLang(b.dataset["l"]);
      c.querySelectorAll<HTMLElement>(".forwho button").forEach((x) => {
        if (x.dataset["w"]) x.setAttribute("aria-pressed", String(x.dataset["w"] === composeFor));
        if (x.dataset["l"])
          x.setAttribute("aria-pressed", String(speechLang().startsWith(x.dataset["l"].slice(0, 2))));
      });
    }),
  );

  const submit = async (): Promise<void> => {
    const text = input.value.trim();
    if (!text) return;
    input.value = "";
    go.disabled = true;

    // SAVE FIRST, PARSE SECOND. The task exists the moment you press send —
    // the model is asked afterwards and upgrades it in place. Nothing typed is
    // ever waiting on a network call, and a slow or failed parse costs nothing
    // but a slightly plainer task. docs/02 §4.2.
    const fallbackOwner =
      composeFor === "all" ? ANYONE : composeFor === "partner" && p ? p.id : me.id;
    let created: Task;
    try {
      created = await api.createTask({
        title: text,
        ownerId: fallbackOwner,
        source: "text",
        rawInput: text,
        // "For all" is a state the parser cannot produce — it returns a member
        // id or nothing — so a deliberate tap on it has to be marked as the
        // human's decision, or the parse that follows would quietly hand the
        // task to somebody.
        ...(composeFor === "all" ? { aiFieldOrigin: { ownerId: "user" } } : {}),
      } as Parameters<typeof api.createTask>[0]);
    } catch (err) {
      // A failed save must be visible, never silent — and the text goes back in
      // the box, because nothing typed is ever lost.
      input.value = text;
      go.disabled = false;
      toast(`Couldn't save — ${(err as ApiError).code}`);
      return;
    }

    await refresh(); // the task is on screen already

    // Then the model. The server only fills fields nobody has edited, so this
    // can take as long as it needs without ever fighting the user.
    void api
      .parse(text, speechLang().startsWith("de") ? "de" : "en")
      .then((parsed) =>
        api.applyParse(created._id, {
          title: parsed.title,
          ownerId: parsed.ownerId,
          dueAt: parsed.dueAt,
          dueIsDateOnly: parsed.dueIsDateOnly,
          trigger: parsed.trigger,
          recurrence: parsed.recurrence,
          category: parsed.category,
        }),
      )
      .then(() => refresh())
      .catch(() => {
        /* a plain task is a perfectly good task */
      });
  };

  go.addEventListener("click", () => void submit());
  input.addEventListener("keydown", (e) => e.key === "Enter" && void submit());

  // ── speech ────────────────────────────────────────────────────────────────
  const mic = c.querySelector<HTMLButtonElement>("#mic");
  if (mic) {
    let session: Listener | null = null;
    const stopUI = (): void => {
      mic.classList.remove("live");
      c.querySelector(".listening")?.remove();
      session = null;
    };

    mic.addEventListener("click", () => {
      if (session) return session.stop();

      mic.classList.add("live");
      c.appendChild(
        el(`<div class="listening">Listening… <span class="pv">${isFragile() ? "Safari can be patchy — Chrome is steadier" : "Audio goes to the browser vendor to be transcribed"}</span></div>`),
      );

      session = listen(
        {
          onInterim: (t) => {
            input.value = t;
            go.disabled = !t.trim();
          },
          onFinal: (t) => {
            // Release-to-save, as on the phone: the sentence becomes a task
            // rather than sitting in a box waiting for a second confirmation.
            input.value = t;
            void submit();
          },
          onError: (code) => {
            stopUI();
            toast(
              code === "not-allowed"
                ? "Microphone blocked — allow it in the address bar"
                : code === "no-speech"
                  ? "Didn't catch that"
                  : `Speech error: ${code}`,
            );
          },
          onEnd: stopUI,
        },
        speechLang(),
      );

      if (!session) {
        stopUI();
        toast("Speech isn't available in this browser");
      }
    });
  }
  return c;
}

// ── detail ───────────────────────────────────────────────────────────────────

const EVENT_LABEL: Record<string, string> = {
  created: "asked", assigned: "assigned it", seen: "saw it", on_it: "started",
  snoozed: "snoozed", blocked: "blocked", completed: "finished it",
  reopened: "reopened it", declined: "declined", edited: "edited", kudos: "said thanks",
  nudged: "nudged",
};

async function detail(id: string): Promise<void> {
  const [t, { events }] = await Promise.all([api.task(id), api.events(id)]);
  // An unowned task is everyone's, so everyone gets the owner's controls.
  const isOwner = t.ownerId === me.id || t.ownerId === null;
  const w = whenLabel(t);

  const scrim = el(`<div class="scrim"><div class="sheet">
      <h3>${esc(t.title)}</h3>
      <div class="mt ${w.cls}">${esc(w.text)}${t.ownerId !== t.requesterId && t.ownerId !== null ? ` · asked by ${esc(nameOf(t.requesterId))}` : ""}</div>
      ${t.rawInput && t.rawInput !== t.title ? `<div class="hint" style="margin-top:.5rem">You said: “${esc(t.rawInput)}”</div>` : ""}
      <button class="ownerrow" id="move">
        <span>${esc(t.ownerId === null ? "Either of us" : t.ownerId === me.id ? "Yours" : `${nameOf(t.ownerId)}’s`)}</span>
        <em>Move</em>
      </button>
      <button class="ownerrow" id="when">
        <span>${esc(whenRowLabel(t))}</span>
        <em>${t.dueAt ? "Change" : "Set"}</em>
      </button>
      <button class="ownerrow" id="label">
        <span>${esc(labelName(t.category) || "No label")}</span>
        <em>${t.category ? "Change" : "Add"}</em>
      </button>
      <label class="notes">
        <span>Notes</span>
        <textarea id="notes" rows="2" placeholder="Anything worth remembering — a shelf number, a phone number, why it matters.">${esc(t.notes ?? "")}</textarea>
      </label>
      <div id="enrich"></div>
      <div class="acts" id="acts"></div>
      <div class="tl">${events
        .map(
          (e) =>
            `<div class="${e.kind === "kudos" ? "k" : ""}"><b>${esc(nameOf(e.actorId))} ${esc(EVENT_LABEL[e.kind] ?? e.kind)}${e.payload["reason"] ? ` — ${esc(String(e.payload["reason"]))}` : ""}</b>${esc(short(e.createdAt))}</div>`,
        )
        .join("")}</div>
    </div></div>`);
  document.body.appendChild(scrim);
  scrim.addEventListener("click", (e) => e.target === scrim && scrim.remove());

  renderEnrichment(scrim, t);

  scrim.querySelector("#move")!.addEventListener("click", () => {
    void saveNotes().then(() => {
      scrim.remove();
      movePanel(t);
    });
  });
  scrim.querySelector("#when")!.addEventListener("click", () => {
    void saveNotes().then(() => {
      scrim.remove();
      whenPanel(t);
    });
  });
  scrim.querySelector("#label")!.addEventListener("click", () => {
    void saveNotes().then(() => {
      scrim.remove();
      labelPanel(t);
    });
  });

  /**
   * Notes save when you look away, not on a Save button. There is no draft
   * state to lose and nothing to forget to press; the only cost is a write per
   * edit, which for two people is nothing.
   */
  const notes = scrim.querySelector<HTMLTextAreaElement>("#notes")!;
  const saveNotes = async (): Promise<void> => {
    const v = notes.value.trim();
    if (v === (t.notes ?? "")) return;
    try {
      await api.patch(t._id, { notes: v === "" ? null : v });
      t.notes = v === "" ? null : v;
      notes.classList.add("saved");
      setTimeout(() => notes.classList.remove("saved"), 900);
      await refresh();
    } catch (e) {
      toast(`Note not saved — ${(e as ApiError).code}`);
    }
  };
  notes.addEventListener("blur", () => void saveNotes());
  // Closing the sheet is the commonest way to leave the field, and blur does
  // not always fire first when the element is removed.
  scrim.addEventListener("click", (e) => {
    if (e.target === scrim) void saveNotes();
  });

  const acts = scrim.querySelector("#acts")!;
  const add = (label: string, cls: string, fn: () => Promise<unknown>) => {
    const b = el(`<button class="${cls}">${esc(label)}</button>`);
    b.addEventListener("click", async () => {
      // A note typed and then "Done" pressed must not vanish: the button steals
      // focus without the textarea's blur landing first.
      await saveNotes();
      try {
        await fn();
      } catch (e) {
        toast((e as ApiError).code);
      }
      scrim.remove();
      await refresh();
    });
    acts.appendChild(b);
  };

  if (isOwner && t.status !== "done") {
    add("Done", "primary", () => api.act(t._id, "complete"));
    if (t.status !== "in_progress") add("On it", "", () => api.act(t._id, "on-it"));
    add("Tonight", "", () => api.act(t._id, "snooze", { reason: "tonight" }));
    add("Tomorrow", "", () => api.act(t._id, "snooze", { reason: "tomorrow" }));
    // Declining is first class and blameless. Not being able to say no makes
    // this a demand queue.
    add("Let's talk", "", () => api.act(t._id, "decline", { reason: "lets_talk" }));
  }
  if (t.status === "done") {
    if (t.completedBy !== me.id) add("♥ Thanks", "warm", () => api.act(t._id, "kudos", { reason: "heart" }));
    if (isOwner) add("Reopen", "", () => api.act(t._id, "reopen"));
  }
  if (!isOwner && t.status !== "done" && t.requesterId === me.id) {
    add("Nudge", "", () => api.act(t._id, "nudge"));
  }
  add("Delete", "", () => api.remove(t._id));
}

/**
 * Tier 2, rendered as proposals.
 *
 * Everything here is offered, never applied: subtasks are accept/dismiss chips,
 * the question is a note rather than a prompt, and the whole block is visibly
 * the app's suggestion rather than something the user wrote.
 */
function renderEnrichment(scrim: HTMLElement, t: Task): void {
  const host = scrim.querySelector("#enrich")!;
  const e = t.enrichment;

  if (!e) {
    const ask = el(`<button class="askhelp" id="ask">Ask for help with this</button>`);
    ask.addEventListener("click", async () => {
      ask.textContent = "Thinking…";
      ask.setAttribute("disabled", "true");
      try {
        const fresh = await api.enrich(t._id);
        host.innerHTML = "";
        renderEnrichment(scrim, fresh);
        if (!fresh.enrichment) toast("Nothing useful to add — it's clear enough");
      } catch (err) {
        ask.removeAttribute("disabled");
        ask.textContent = "Ask for help with this";
        toast((err as ApiError).code);
      }
    });
    host.appendChild(ask);
    return;
  }

  const box = el(`<div class="advice">
      <div class="advice-h">Duly suggests</div>
      ${e.explanation ? `<p class="advice-x">${esc(e.explanation)}</p>` : ""}
      ${e.prep?.length ? `<div class="prep"><span>Have ready</span>${e.prep.map((p) => `<span class="chip">${esc(p)}</span>`).join("")}</div>` : ""}
      ${e.openQuestion ? `<p class="ask">${esc(e.openQuestion)}</p>` : ""}
      ${e.subtasks?.length ? `<div class="steps" id="steps"></div>` : ""}
      ${e.effort || e.context?.length ? `<div class="meta">${e.effort ? `<span class="chip">${esc(e.effort)}</span>` : ""}${(e.context ?? []).map((c) => `<span class="chip">${esc(c.replace(/_/g, " "))}</span>`).join("")}</div>` : ""}
    </div>`);
  host.appendChild(box);

  const steps = box.querySelector("#steps");
  if (!steps) return;
  for (const s of e.subtasks ?? []) {
    const chip = el(`<div class="step ${s.accepted ? "accepted" : ""}">
        <span>${esc(s.title)}</span>
        ${s.accepted ? `<em>added</em>` : `<button class="yes" title="Add as a task">＋</button><button class="no" title="Not needed">×</button>`}
      </div>`);
    chip.querySelector(".yes")?.addEventListener("click", async () => {
      await api.subtask(t._id, s.title, true);
      chip.classList.add("accepted");
      chip.innerHTML = `<span>${esc(s.title)}</span><em>added</em>`;
      await refresh();
    });
    chip.querySelector(".no")?.addEventListener("click", async () => {
      await api.subtask(t._id, s.title, false);
      chip.remove();
    });
    steps.appendChild(chip);
  }
}

// ── boot ─────────────────────────────────────────────────────────────────────

async function refresh(): Promise<void> {
  // Both views, always. They are two readings of the same small list, and
  // fetching only the active one made every mode switch flash a spinner.
  const [dash, cal] = await Promise.all([api.dashboard(sort), api.calendar()]);
  sections = dash.sections;
  calendar = cal;
  render();
}

async function boot(): Promise<void> {
  if (!token.get()) await api.registerDevice();
  try {
    const r = await api.me();
    me = r.member;
    members = r.members;
    household = r.household;
  } catch (e) {
    if ((e as ApiError).status === 401) {
      token.clear();
      await api.registerDevice();
      const r = await api.me();
      me = r.member;
      members = r.members;
      household = r.household;
    } else throw e;
  }
  if (!household) return onboarding();
  await refresh();
}

// A /j/CODE link drops straight into the join screen with the code filled in.
// The worker must be alive to receive a push at all, so it is registered on
// boot rather than when settings happens to be open. It caches nothing.
void ensureWorker();

const joinMatch = location.pathname.match(/^\/j\/([A-Za-z0-9-]+)/);
/** Where a tapped notification lands. The service worker sends /t/<id>. */
const taskMatch = location.pathname.match(/^\/t\/([A-Za-z0-9_-]+)/);

void boot().then(async () => {
  if (joinMatch && !household) {
    joinScreen();
    const f = document.querySelector<HTMLInputElement>("#code");
    if (f) f.value = joinMatch[1]!;
    return;
  }
  if (taskMatch && household) {
    // Put the address bar back before opening the sheet: closing it would
    // otherwise leave the URL pointing at a task that is no longer on screen,
    // and a reload would reopen it.
    history.replaceState(null, "", "/");
    // A notification for a task that has since been deleted opens the list
    // rather than an error — the task being gone is usually the good outcome.
    await detail(taskMatch[1]!).catch(() => toast("That task is gone"));
  }
});
